import { useCallback, useEffect, useState } from "react";
import { deleteField, doc, onSnapshot, updateDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";

export type RaisedHand = {
  uid: string;
  name: string;
  raisedAtMs: number;
};

const HAND_ATTR = "data-constela-hand";

const HAND_CSS = `
[${HAND_ATTR}] {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #f5c542;
  color: #1f1400;
  font-size: 16px;
  line-height: 1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  pointer-events: none;
}
`;

type HandTile = HTMLElement & {
  participant?: { customParticipantId?: string };
};

const TILE_TAGS = new Set(["rtk-participant-tile", "dyte-participant-tile"]);

function tileUid(el: HTMLElement): string {
  const uid = (el as HandTile).participant?.customParticipantId;
  return typeof uid === "string" ? uid : "";
}

function walkTiles(root: ParentNode, onTile: (el: HTMLElement) => void): void {
  const walk = (node: ParentNode) => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));
    for (const el of elements) {
      if (!(el instanceof HTMLElement)) continue;
      if (TILE_TAGS.has(el.tagName.toLowerCase())) onTile(el);
      if (el.shadowRoot) walk(el.shadowRoot);
    }
  };
  walk(root);
}

export function markRaisedHandTiles(root: ParentNode, uids: ReadonlySet<string>): number {
  let marked = 0;
  walkTiles(root, (el) => {
    const shadow = el.shadowRoot;
    if (!shadow) return;
    let badge = shadow.querySelector(`[${HAND_ATTR}]`);
    if (!badge) {
      const style = document.createElement("style");
      style.setAttribute("data-constela-hand-style", "true");
      style.textContent = HAND_CSS;
      if (!shadow.querySelector("style[data-constela-hand-style]")) {
        shadow.appendChild(style);
      }
      badge = document.createElement("span");
      badge.setAttribute(HAND_ATTR, "true");
      badge.setAttribute("aria-hidden", "true");
      badge.textContent = "✋";
      shadow.appendChild(badge);
    }
    const raised = uids.has(tileUid(el));
    const node = badge as HTMLElement;
    node.style.display = raised ? "inline-flex" : "none";
    if (raised) marked += 1;
  });
  return marked;
}

export const HAND_NOTICE_MS = 4000;

export function handNoticeKey(hand: RaisedHand): string {
  return `${hand.uid}:${hand.raisedAtMs}`;
}

function readRaisedAtMs(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object" && "toMillis" in value) {
    const millis = (value as { toMillis: () => number }).toMillis();
    return typeof millis === "number" && Number.isFinite(millis) ? millis : 0;
  }
  return 0;
}

/** O aviso de um pedido não volta. Um novo levantar de mão gera outro aviso. */
export function openHandNotices(
  hands: readonly RaisedHand[],
  dismissedKeys: ReadonlySet<string>,
): RaisedHand[] {
  return hands.filter((hand) => hand.raisedAtMs > 0 && !dismissedKeys.has(handNoticeKey(hand)));
}

export function useRaisedHands(roomId: string, myUid: string | undefined) {
  const [hands, setHands] = useState<RaisedHand[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!roomId) return;
    const participantsRef = collection(db, "rooms", roomId, "participants");
    return onSnapshot(participantsRef, (snapshot) => {
      const next: RaisedHand[] = [];
      snapshot.forEach((participantDoc) => {
        const data = participantDoc.data();
        if (data.handRaised !== true) return;
        next.push({
          uid: participantDoc.id,
          name: typeof data.name === "string" && data.name.trim() ? data.name.trim() : "Participante",
          raisedAtMs: readRaisedAtMs(data.handRaisedAt),
        });
      });
      next.sort((a, b) => a.raisedAtMs - b.raisedAtMs);
      setHands(next);
    });
  }, [roomId]);

  const mine = !!myUid && hands.some((hand) => hand.uid === myUid);

  const toggle = useCallback(async () => {
    if (!roomId || !myUid || busy) return;
    setBusy(true);
    try {
      const participantRef = doc(db, "rooms", roomId, "participants", myUid);
      if (mine) {
        await updateDoc(participantRef, {
          handRaised: false,
          handRaisedAt: deleteField(),
        });
      } else {
        await updateDoc(participantRef, {
          handRaised: true,
          handRaisedAt: Date.now(),
        });
      }
    } finally {
      setBusy(false);
    }
  }, [roomId, myUid, busy, mine]);

  return { hands, mine, busy, toggle };
}
